import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import Table, {
  buttonsStyle,
  dateFormatter,
} from "../../../components/tables/table";
import { Button, TextField, MenuItem, Tooltip } from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import { alertError } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { getTradingOperations } from "../../../../api/tradingOperations";

const STATUS_OPTIONS = [
  "",
  "pending",
  "submitted",
  "active",
  "partially_closed",
  "closed",
  "canceled",
  "failed",
  "logged",
];

const SOURCE_OPTIONS = ["", "hub", "bot"];

const STRATEGY_OPTIONS = [
  "",
  "Hedge",
  "Scalping",
  "Slope",
  "Rebuys",
  "unknown",
];

const emptyFilters = () => ({
  status: "",
  exchange: "",
  symbol: "",
  source: "",
  strategy: "",
  from: "",
  to: "",
});

function parseOpenPriceFromSnapshot(snap) {
  if (!snap || typeof snap !== "object") return null;
  const raw = snap.price;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function formatOpenPriceDisplay(item) {
  const n = parseOpenPriceFromSnapshot(item?.requestSnapshot);
  if (n != null) return String(n);
  const raw = item?.requestSnapshot?.price;
  return raw != null && raw !== "" ? String(raw) : "—";
}

/** Solo el número de precio de cierre (columna). */
function formatClosePriceCell(item) {
  if (
    item.status !== "closed" ||
    !item.closeRecord ||
    typeof item.closeRecord !== "object"
  ) {
    return "—";
  }
  const cr = item.closeRecord;
  const px = cr.closePrice != null ? Number(cr.closePrice) : NaN;
  if (Number.isFinite(px)) return String(px);
  if (cr.closePrice != null && cr.closePrice !== "")
    return String(cr.closePrice);
  return "—";
}

/** PnL realizado Zoomex (USDT) desde closeRecord.zoomexClosedPnl. */
function formatZoomexPnlCell(item) {
  if (
    item.status !== "closed" ||
    !item.closeRecord ||
    typeof item.closeRecord !== "object"
  ) {
    return "—";
  }
  const z = item.closeRecord.zoomexClosedPnl;
  if (!z || typeof z !== "object") return "—";
  if (z.fetchError) return "—";
  const s = z.closedPnlSum;
  if (s != null && String(s).trim() !== "") return String(s).trim();
  return "—";
}

function formatZoomexPnlTooltip(item) {
  const parts = [];
  if (
    item.status === "closed" &&
    item.closeRecord &&
    typeof item.closeRecord === "object"
  ) {
    const z = item.closeRecord.zoomexClosedPnl;
    if (z && typeof z === "object") {
      if (z.fetchError) parts.push(`Error API: ${z.fetchError}`);
      if (z.avgExitPrice != null && z.avgExitPrice !== "")
        parts.push(`Precio salida medio: ${z.avgExitPrice}`);
      if (Array.isArray(z.orderIds) && z.orderIds.length)
        parts.push(`Órdenes cierre: ${z.orderIds.join(", ")}`);
      if (z.rowCount != null) parts.push(`Filas PnL: ${z.rowCount}`);
      if (z.fetchedAt) {
        try {
          parts.push(`Consulta PnL: ${new Date(z.fetchedAt).toLocaleString()}`);
        } catch {
          parts.push(`Consulta PnL: ${z.fetchedAt}`);
        }
      }
    }
  }
  return parts.length ? parts.join(" · ") : "";
}

function zoomexPnlFormatter(cell, row) {
  const tip = row?.zoomexPnlTooltip || "";
  if (!tip) return <span>{cell}</span>;
  return (
    <Tooltip title={tip}>
      <span style={{ cursor: "help" }}>{cell}</span>
    </Tooltip>
  );
}

/** Fecha, motivo y origen para tooltip (no se muestra en la celda). */
function formatCloseDetailTooltip(item) {
  const parts = [];
  if (
    item.status === "closed" &&
    item.closeRecord &&
    typeof item.closeRecord === "object"
  ) {
    const cr = item.closeRecord;
    if (cr.at) {
      try {
        parts.push(new Date(cr.at).toLocaleString());
      } catch {
        parts.push(String(cr.at));
      }
    }
    if (cr.closeReason) parts.push(String(cr.closeReason));
    if (cr.outcome) parts.push(String(cr.outcome));
    if (cr.source) parts.push(String(cr.source));
    const z = cr.zoomexClosedPnl;
    if (z && typeof z === "object" && z.closedPnlSum != null && z.closedPnlSum !== "")
      parts.push(`PnL Zoomex: ${z.closedPnlSum}`);
  } else if (
    item.status === "canceled" &&
    item.cancelRecord &&
    typeof item.cancelRecord === "object"
  ) {
    const c = item.cancelRecord;
    parts.push("Cancelación");
    if (c.at) {
      try {
        parts.push(new Date(c.at).toLocaleString());
      } catch {
        parts.push(String(c.at));
      }
    }
    if (c.cancelReason) parts.push(String(c.cancelReason));
    if (c.source) parts.push(String(c.source));
  }
  return parts.length ? parts.join(" · ") : "";
}

function closeInfoFormatter(cell, row) {
  const tip = row?.closeInfoTooltip || "";
  if (!tip) return <span>{cell}</span>;
  return (
    <Tooltip title={tip}>
      <span style={{ cursor: "help" }}>{cell}</span>
    </Tooltip>
  );
}

function buildRow(item) {
  const ext = Array.isArray(item.externalOrderIds)
    ? item.externalOrderIds.join(", ")
    : "";
  return {
    id: String(item._id),
    _id: item._id,
    createdAt: item.createdAt,
    exchange: item.exchange || "—",
    symbol: item.symbol || "—",
    side: item.side || "—",
    status: item.status || "—",
    strategy: item.strategy || "—",
    source: item.source || "—",
    openPrice: formatOpenPriceDisplay(item),
    closeInfo: formatClosePriceCell(item),
    closeInfoTooltip: formatCloseDetailTooltip(item),
    zoomexPnl: formatZoomexPnlCell(item),
    zoomexPnlTooltip: formatZoomexPnlTooltip(item),
    externalIdsShort: ext.length > 40 ? `${ext.slice(0, 40)}…` : ext || "—",
  };
}

export default function TradingOperationsPage() {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);

  const [draft, setDraft] = useState(() => emptyFilters());
  const [applied, setApplied] = useState(() => emptyFilters());

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit, skip };
    if (applied.status) params.status = applied.status;
    if (applied.exchange.trim()) params.exchange = applied.exchange.trim();
    if (applied.symbol.trim()) params.symbol = applied.symbol.trim();
    if (applied.source) params.source = applied.source;
    if (applied.strategy) params.strategy = applied.strategy;
    if (applied.from) params.from = new Date(applied.from).toISOString();
    if (applied.to) params.to = new Date(applied.to).toISOString();

    getTradingOperations(params)
      .then((res) => {
        if (res.status === 200 && res.data) {
          const rows = (res.data.items || []).map(buildRow);
          setItems(rows);
          setTotal(
            typeof res.data.total === "number" ? res.data.total : rows.length,
          );
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se pudieron cargar las operaciones de trading.",
        });
      })
      .finally(() => setLoading(false));
  }, [limit, skip, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setSkip(0);
  };

  const resetFilters = () => {
    const z = emptyFilters();
    setDraft(z);
    setApplied(z);
    setSkip(0);
  };

  function actionsFormatter(cell) {
    return (
      <Tooltip title="Ver detalle">
        <Button
          style={buttonsStyle}
          size="small"
          onClick={() => history.push(`/trading-operations/${cell}`)}
        >
          <Visibility />
        </Button>
      </Tooltip>
    );
  }

  const columns = [
    { dataField: "createdAt", text: "Fecha", formatter: dateFormatter },
    { dataField: "exchange", text: "Exchange" },
    { dataField: "symbol", text: "Símbolo" },
    { dataField: "side", text: "Lado" },
    { dataField: "status", text: "Estado" },
    { dataField: "openPrice", text: "Apertura" },
    {
      dataField: "closeInfo",
      text: "Cierre",
      formatter: closeInfoFormatter,
    },
    {
      dataField: "zoomexPnl",
      text: "PnL cierre (Zoomex)",
      formatter: zoomexPnlFormatter,
    },
    { dataField: "strategy", text: "Estrategia" },
    { dataField: "source", text: "Origen" },
    { dataField: "externalIdsShort", text: "Ids externos" },
    {
      dataField: "id",
      text: "",
      formatter: actionsFormatter,
      headerStyle: { width: "80px" },
    },
  ];

  const page = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const setDraftField = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setDraft((d) => ({ ...d, [key]: value }));
  };

  return (
    <Card>
      <CardHeader title="Operaciones de trading" />
      <CardBody>
        <div className="row mb-4">
          <div className="col-md-3 mb-2">
            <TextField
              select
              fullWidth
              label="Estado"
              value={draft.status}
              onChange={setDraftField("status")}
              variant="outlined"
              size="small"
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s || "all"} value={s}>
                  {s || "(todos)"}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Exchange"
              value={draft.exchange}
              onChange={setDraftField("exchange")}
              placeholder="Zoomex"
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Símbolo"
              value={draft.symbol}
              onChange={setDraftField("symbol")}
              placeholder="BTC-USDT"
              variant="outlined"
              size="small"
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              select
              fullWidth
              label="Origen"
              value={draft.source}
              onChange={setDraftField("source")}
              variant="outlined"
              size="small"
            >
              {SOURCE_OPTIONS.map((s) => (
                <MenuItem key={s || "all"} value={s}>
                  {s || "(todos)"}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              select
              fullWidth
              label="Estrategia"
              value={draft.strategy}
              onChange={setDraftField("strategy")}
              variant="outlined"
              size="small"
            >
              {STRATEGY_OPTIONS.map((s) => (
                <MenuItem key={s || "all"} value={s}>
                  {s || "(todas)"}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Desde"
              type="datetime-local"
              value={draft.from}
              onChange={setDraftField("from")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="col-md-3 mb-2">
            <TextField
              fullWidth
              label="Hasta"
              type="datetime-local"
              value={draft.to}
              onChange={setDraftField("to")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <div className="col-md-3 mb-2 d-flex align-items-end">
            <Button
              color="primary"
              variant="contained"
              onClick={applyFilters}
              disabled={loading}
            >
              Aplicar filtros
            </Button>
            <Button className="ml-2" onClick={resetFilters} disabled={loading}>
              Limpiar
            </Button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="text-muted">
            Total: {total} · Página {page} de {totalPages}
            {loading ? " · Cargando…" : ""}
          </span>
          <div>
            <Button
              size="small"
              disabled={skip <= 0 || loading}
              onClick={() => setSkip(Math.max(0, skip - limit))}
            >
              Anterior
            </Button>
            <Button
              size="small"
              className="ml-2"
              disabled={skip + limit >= total || loading}
              onClick={() => setSkip(skip + limit)}
            >
              Siguiente
            </Button>
          </div>
        </div>

        <Table data={items} columns={columns} search={false} paginate={false} />
      </CardBody>
    </Card>
  );
}
